import isEmpty from "lodash/isEmpty";
import { FC, useRef } from "react";
import { IdNameList } from "../../../../../../../../api/services/common/models";
import {
  MuiBox,
  MuiDoubleLabel,
  MuiGrid,
  MuiTypography,
} from "../../../../../../../../components";
import { ChipOptionsDataType } from "../../../../../../../../components/chip/MuiChip";
import MuiChipWithPopover from "../../../../../../../../components/chipWithPopover/MuiChipWithPopover";
import { MuiInfoToolTip } from "../../../../../../../../components/tooltip/infoToolTip/MuiInfoToolTip";
import { EMPTY_VALUE_DASH } from "../../../../../../../../helpers/constants";
import {
  formatDate,
  formatOptions,
  getFormattedSalary,
  getValueOrBlank,
  mapTrueValues,
} from "../../../../../../../../helpers/utils";
import { useGridContainer } from "../../../../../../../../hooks/useGridContainer";
import { useMediaQueries } from "../../../../../../../../hooks/useMediaQueries";
import { useNavigationSiteCore } from "../../../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import {
  OccupationalFunctionOtherKey,
  ParticipantDetail,
} from "../../../../../../../createProgram/modelsAndSchemas/Models";
import { programSiteCoreKeys } from "../../../../../../../createProgram/siteCore/SiteCoreKeys";
import { CareerGoalsWrapper } from "./CareerGoals.style";
import { useGetLoggedInUserDetails } from "../../../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { getDateFormatByCountry } from "../../../../../../../../helpers/constants/countryDateFormat";

interface CareerGoalsProp {
  participantDetail?: ParticipantDetail;
}
export const CareerGoals: FC<CareerGoalsProp> = ({ participantDetail }) => {
  const { gridSizeMedium: gridSize, gridSizeMediumClass } = useGridContainer();

  const gridRef = useRef<HTMLDivElement>(null);
  const { fetchLabel, fetchValueByKeyOfDropdown } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { isMobile } = useMediaQueries();
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    getValueOrBlank(loggedInUser?.countryCode),
  );

  const getLabel = (key: string) => {
    return fetchLabel(programSiteCoreKeys.careerGoal, key);
  };

  const selectPreferencesDetails = {
    participantNotSeekingEmployment:
      participantDetail?.participantNotSeekingEmployment,
    participantNotWillngToWorkRemotely:
      participantDetail?.participantNotWillngToWorkRemotely,
    openToRelocation: participantDetail?.openToRelocation,
    participantWillingToTravelForWork:
      participantDetail?.participantWillingToTravelForWork,
  };

  const mappingSelectPreferencesDetails = {
    participantNotSeekingEmployment: getLabel(
      programSiteCoreKeys.participantNotSeekingEmployment,
    ),
    participantNotWillngToWorkRemotely: getLabel(
      programSiteCoreKeys.participantWillingToWorkRemotely,
    ),
    openToRelocation: getLabel(programSiteCoreKeys.openToRelocation),
    participantWillingToTravelForWork: getLabel(
      programSiteCoreKeys.participantWillingToTravelForWork,
    ),
  };

  const selectPreferencesDetailsValue = mapTrueValues(
    selectPreferencesDetails,
    mappingSelectPreferencesDetails,
  );
  const authorizedConnection = {
    experis: participantDetail?.experis,
    manpowerEngineering: participantDetail?.manpowerEngineering,
    jeffersonWells: participantDetail?.jeffersonWells,
    rpo: participantDetail?.rpo,
  };
  const mappingAuthorizedConnection = {
    experis: getLabel(programSiteCoreKeys.experis),
    manpowerEngineering: getLabel(programSiteCoreKeys.manPowerEngineering),
    jeffersonWells: getLabel(programSiteCoreKeys.jeffersonWells),
    rpo: getLabel(programSiteCoreKeys.rpo),
  };
  const authorizedConnectionValue = mapTrueValues(
    authorizedConnection,
    mappingAuthorizedConnection,
  );

  const salaryRange = fetchValueByKeyOfDropdown(
    programSiteCoreKeys.careerGoal,
    programSiteCoreKeys.salaryRangeList,
    getValueOrBlank(participantDetail?.salaryRangeId),
  );

  const isCurrencyName = getValueOrBlank(participantDetail?.currencyName);

  const getValue = (value?: string) => {
    return isEmpty(value) ? EMPTY_VALUE_DASH : value;
  };

  return (
    <CareerGoalsWrapper>
      <MuiGrid
        container
        data-testid="sponsoring-employer"
        className={isMobile ? "content-target-job-title" : "content"}
      >
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            replaceEmptyWithDash
            label={getLabel(programSiteCoreKeys.targetJobTitle)}
            value={getValue(participantDetail?.targetJobTitle)}
            isValueWithEllipsis
          />
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            replaceEmptyWithDash
            label={getLabel(programSiteCoreKeys.targetOccupationalFunction)}
            value={fetchValueByKeyOfDropdown(
              programSiteCoreKeys.careerGoal,
              programSiteCoreKeys.occupationalFunctionListCareerGoal,
              getValueOrBlank(participantDetail?.targetOccupationFunctionId),
            )}
            isValueWithEllipsis
          />
        </MuiGrid>
        {participantDetail?.targetOccupationFunctionId ===
          OccupationalFunctionOtherKey && (
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDoubleLabel
              replaceEmptyWithDash
              label={getLabel(
                programSiteCoreKeys.targetOccupationalFunctionOther,
              )}
              value={getValue(participantDetail?.targetOccupationOtherFunction)}
              isValueWithEllipsis
            />
          </MuiGrid>
        )}
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            replaceEmptyWithDash
            label={getLabel(programSiteCoreKeys.targetOccupationalLevel)}
            value={fetchValueByKeyOfDropdown(
              programSiteCoreKeys.careerGoal,
              programSiteCoreKeys.occupationalLevelListCareerGoal,
              getValueOrBlank(participantDetail?.targetOccupationLevelId),
            )}
            isValueWithEllipsis
          />
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            replaceEmptyWithDash
            label={getLabel(programSiteCoreKeys.targetIndustryCareerGoal)}
            value={getValue(participantDetail?.targetIndustry)}
            isValueWithEllipsis
          />
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            replaceEmptyWithDash
            label={getLabel(programSiteCoreKeys.targetOrganization)}
            value={getValue(participantDetail?.targetOrganisation)}
            isValueWithEllipsis
          />
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            replaceEmptyWithDash
            label={getLabel(programSiteCoreKeys.jobType)}
            value={fetchValueByKeyOfDropdown(
              programSiteCoreKeys.careerGoal,
              programSiteCoreKeys.jobTypeList,
              getValueOrBlank(participantDetail?.targetJobTypeId),
            )}
            isValueWithEllipsis
          />
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            replaceEmptyWithDash
            label={getLabel(programSiteCoreKeys.targetLocation)}
            value={getValue(participantDetail?.targetLocation)}
            isValueWithEllipsis
          />
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            replaceEmptyWithDash
            label={getLabel(programSiteCoreKeys.targetSalaryRange)}
            value={getFormattedSalary(isCurrencyName, salaryRange)}
            isValueWithEllipsis
          />
        </MuiGrid>
      </MuiGrid>
      <MuiGrid
        container
        spacing={isMobile ? 0 : 2}
        className={isMobile ? "content-skill-keywords" : "content"}
      >
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiTypography
            component={"h3"}
            className="label-style"
            variant={"h3"}
          >
            {getLabel(programSiteCoreKeys.skillsKeywords)}
          </MuiTypography>
          {!isEmpty(participantDetail?.skills) ? (
            <MuiChipWithPopover
              data={
                participantDetail?.skills?.map((item: IdNameList) => ({
                  label: item.name,
                  value: item.id,
                })) as ChipOptionsDataType[]
              }
              handleDelete={() => {}}
              isEditable={false}
              headerLabel={getLabel(programSiteCoreKeys.skillsKeywords)}
              gridRef={gridRef}
              showMoreARIALabel={commonARIALabel.showMoreDetails}
              removeChipNameARIALabel={commonARIALabel.removeChipName}
              closePopoverARIALabel={commonARIALabel.closePopover}
            />
          ) : (
            <MuiTypography
              component={"h4"}
              className="value-style"
              variant={"h4Bold"}
            >
              {EMPTY_VALUE_DASH}
            </MuiTypography>
          )}
        </MuiGrid>
        <MuiGrid size={gridSize} ref={gridRef} className={gridSizeMediumClass}>
          <MuiTypography
            component={"h3"}
            className="label-style"
            variant={"h3"}
          >
            {getLabel(programSiteCoreKeys.technicalSkills)}
          </MuiTypography>
          {!isEmpty(participantDetail?.technicalSkills) ? (
            <MuiChipWithPopover
              data={
                participantDetail?.technicalSkills?.map((item: IdNameList) => ({
                  label: item.name,
                  value: item.id,
                })) as ChipOptionsDataType[]
              }
              handleDelete={() => {}}
              isEditable={false}
              headerLabel={getLabel(programSiteCoreKeys.technicalSkills)}
              gridRef={gridRef}
              showMoreARIALabel={commonARIALabel.showMoreDetails}
              removeChipNameARIALabel={commonARIALabel.removeChipName}
              closePopoverARIALabel={commonARIALabel.closePopover}
            />
          ) : (
            <MuiTypography
              component={"h4"}
              className="value-style"
              variant={"h4Bold"}
            >
              {EMPTY_VALUE_DASH}
            </MuiTypography>
          )}
        </MuiGrid>
      </MuiGrid>

      <MuiGrid container spacing={isMobile ? 0 : 2} className="content">
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            replaceEmptyWithDash
            label={getLabel(
              programSiteCoreKeys.participantReadyToStartJobSearch,
            )}
            value={fetchValueByKeyOfDropdown(
              programSiteCoreKeys.careerGoal,
              programSiteCoreKeys.participantStartJobSearchList,
              participantDetail?.isMarketReady
                ? programSiteCoreKeys.participantStartJobSearchYes
                : programSiteCoreKeys.participantStartJobSearchNo,
            )}
            isValueWithEllipsis
          />
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            replaceEmptyWithDash
            label={getLabel(programSiteCoreKeys.participantJobSearchStartDate)}
            value={
              participantDetail?.marketReadinessDate
                ? formatDate(
                    participantDetail?.marketReadinessDate,
                    userCountryDateFormat,
                  )
                : ""
            }
            isValueWithEllipsis
          />
        </MuiGrid>
      </MuiGrid>

      <MuiGrid container spacing={isMobile ? 0 : 2} className="content">
        <MuiGrid
          size={gridSize}
          className={`${gridSizeMediumClass} preferences-section`}
        >
          <MuiTypography
            component={"h3"}
            className="label-style"
            variant={"h3"}
          >
            {getLabel(programSiteCoreKeys.preferences)}
          </MuiTypography>
          {!isEmpty(selectPreferencesDetailsValue) ? (
            <MuiChipWithPopover
              data={formatOptions(selectPreferencesDetailsValue)}
              handleDelete={() => {}}
              isEditable={false}
              headerLabel={getLabel(programSiteCoreKeys.preferences)}
              gridRef={gridRef}
              showMoreARIALabel={commonARIALabel.showMoreDetails}
              removeChipNameARIALabel={commonARIALabel.removeChipName}
              closePopoverARIALabel={commonARIALabel.closePopover}
            />
          ) : (
            <MuiTypography
              component={"h4"}
              className="value-style"
              variant={"h4Bold"}
            >
              {EMPTY_VALUE_DASH}
            </MuiTypography>
          )}
        </MuiGrid>
        <MuiGrid
          size={gridSize}
          ref={gridRef}
          className={`${gridSizeMediumClass} authorized-connections-section`}
        >
          <MuiBox className="authorized-connections">
            <MuiTypography
              component={"h3"}
              className="label-style"
              variant={"h3"}
            >
              {getLabel(programSiteCoreKeys.authorisedConnections)}
            </MuiTypography>
            <MuiInfoToolTip
              isRemoveCloseIcon
              message={getLabel(programSiteCoreKeys.popUpInfo)}
            />
          </MuiBox>
          {!isEmpty(authorizedConnectionValue) ? (
            <MuiChipWithPopover
              data={formatOptions(authorizedConnectionValue)}
              handleDelete={() => {}}
              isEditable={false}
              headerLabel={getLabel(programSiteCoreKeys.authorisedConnections)}
              gridRef={gridRef}
              showMoreARIALabel={commonARIALabel.showMoreDetails}
              removeChipNameARIALabel={commonARIALabel.removeChipName}
              closePopoverARIALabel={commonARIALabel.closePopover}
            />
          ) : (
            <MuiTypography
              component={"h4"}
              className="value-style"
              variant={"h4Bold"}
            >
              {EMPTY_VALUE_DASH}
            </MuiTypography>
          )}
        </MuiGrid>
      </MuiGrid>
    </CareerGoalsWrapper>
  );
};
